import { computed, ref, Ref, watch, nextTick } from 'vue';
import { SearchStudyCardInfo } from '@/study-search/domain';
import { ParameterType, PostHogExperiment } from '@/study-search/enums';
import getters from '@/study-search/store/getters';
import { defineAdSizeAndSlot, includeBannersInList } from './utils';
import store from '@/study-search/store';
import { IImpressionValidator, ImpressionValidator } from '@studyportals/impression-validator';
import { ApplicationStatusOptions } from '@/study-search/models/application-status/ApplicationStatusOptions';
import { BannerTypes } from '@/study-search/models/banner/IGoogleTag';
import { PresentationBannerInfo } from '@/study-search/models/banner/PresentationBannerInfo';
import { PresentationBannerClusterInfo } from '@/study-search/models/banner/cluster/PresentationBannerClusterInfo';
import { PaginationDefinition } from '@/study-search/models/definition';
import { IncentiveVisibilityHelper } from '@/study-search/models/iu';
import { PresentationStudyCardInfo } from '@/study-search/models/search-result/PresentationStudyCardInfo';
import { TSearchResult } from '@/study-search/models/search-result/TSearchResult';
import { PortalType } from '@studyportals/domain-client';
import { Prerender } from '@/study-search/models/prerender/Prerender';
import { RoutingHelper } from '@/study-search/router';
import { SearchObserver } from '@/study-search/platform/SearchObserver';
import globals from '@/study-search/utils/globals';
import { CopilotEntrypointType, CopilotStudyLevel, PromptVersion, RenderEntrypointCopilotCommand } from '@studyportals/copilot-service-interfaces';

export default class SearchResults {
	public showSearchHelper: Ref<boolean>;
	public searchResultsElement: HTMLCollection;
	public readonly nextPageHref: Ref<string> = ref('');
	public readonly previousPageHref: Ref<string> = ref('');
	private hasShownSophiaVariationA = false;
	private observerForVariationA?: IntersectionObserver;
	private searchResultsObserver: SearchObserver;

	constructor() {
		this.pagesWithAdSlotsDefined = [];
		this.showSearchHelper = ref(false);
		this.resultsPerPage = PaginationDefinition.PAGE_SIZE;
		this.searchResultsObserver = new SearchObserver('SearchResultItem');
	}

	postHogExperimentValues = PostHogExperiment
	currentPostHogExperiment = computed((): PostHogExperiment | undefined => {
		return store.getters.getCurrentPostHogExperiment();
	});

	public async mounted(): Promise<void> {
		this.toggleSearchHelper();
		this.initializeObserver();

		await this.updateHrefLinks();

		this.searchResultsElement = this.searchResultsRef.value?.firstElementChild?.children as HTMLCollection;

		watch([this.amountShown, this.totalResults], this.updatePaginationBar.bind(this));

		watch(this.searchResults, async () => {
			await nextTick();
			document.dispatchEvent(new CustomEvent('Experiment:SearchResultsMounted'));

			if (window.PortalType !== PortalType.MASTER) return;

			this.showSophiaVariation();
		});
	}

	public beforeUnmount(): void {
		this.searchResultsObserver.disconnect();
	}

	private initializeObserver(): void {
		this.searchResultsObserver.observe('.SearchResultsList', () => {
			Prerender.instance.setPrerenderReady();
		});
	}

	private showSophiaVariation(): void {
		const currentExperiment = this.currentPostHogExperiment.value;
		if (!currentExperiment) return;

		if (currentExperiment === PostHogExperiment.SOPHIA_ENTRY_POINT_VARIANT_A) this.setupScrollListenerForVariationA();
		if (currentExperiment === PostHogExperiment.SOPHIA_ENTRY_POINT_VARIANT_B) this.showSophiaVariationB();
	}

	private setupScrollListenerForVariationA(): void {
		const currentExperiment = this.currentPostHogExperiment.value;
		if (currentExperiment !== PostHogExperiment.SOPHIA_ENTRY_POINT_VARIANT_A) return;

		if (this.observerForVariationA) {
			this.observerForVariationA.disconnect();
		}

		const searchCards = this.searchResultsRef.value?.querySelectorAll('.SearchStudyCard');
		const eleventhCard = searchCards?.[10];

		if (!eleventhCard) return;

		this.observerForVariationA = new IntersectionObserver((entries, observer) => {
			entries.forEach(entry => {
				if (entry.isIntersecting && !this.hasShownSophiaVariationA) {
					this.showSophiaVariationA();
					this.hasShownSophiaVariationA = true;
					observer.disconnect();
				}
			});
		}, { threshold: 0.5 });

		this.observerForVariationA.observe(eleventhCard);
	}

	private showSophiaVariationA(): void {
		const element = document.getElementById('copilot-entry-point-a');
		if (!element) return;

		const command = new RenderEntrypointCopilotCommand(
			CopilotEntrypointType.SEARCH_PAGE_VARIATION_A,
			element,
			CopilotStudyLevel.MASTER,
			undefined,
			PromptVersion.SEARCH_PAGE
		);
		globals.copilotService.renderEntrypoint(command);
	}

	private showSophiaVariationB(): void {
		const element = document.getElementById('copilot-entry-point-b');
		if (!element) return;
		const command = new RenderEntrypointCopilotCommand(
			CopilotEntrypointType.SEARCH_PAGE_VARIATION_B,
			element,
			CopilotStudyLevel.MASTER,
			undefined,
			PromptVersion.SEARCH_PAGE
		);
		globals.copilotService.renderEntrypoint(command);
	}

	public loading = computed(() => {
		return store.getters.getApplicationStatus() as ApplicationStatusOptions !== ApplicationStatusOptions.READY;
	});

	public isLastPage = computed((): boolean => {
		return this.totalPages.value === this.currentPageNumber.value;
	});

	public applicationStatus = computed((): ApplicationStatusOptions => {
		return getters.getApplicationStatus() as ApplicationStatusOptions;
	});

	public applicationStatusReady = computed((): boolean => {
		return getters.getApplicationStatus() as ApplicationStatusOptions === ApplicationStatusOptions.READY;
	});

	public showRecommendations = computed((): boolean => {
		return getters.getApplicationStatus() as ApplicationStatusOptions === ApplicationStatusOptions.READY;
	});

	public filterKeywordValue = computed((): string | null => {
		const keywordWhat = getters.getFilterValues(ParameterType.KEYWORD_WHAT)[0];
		const keywordWhere = getters.getFilterValues(ParameterType.KEYWORD_WHERE)[0];
		if (keywordWhat && keywordWhere) {
			return keywordWhat + ' ' + keywordWhere;
		}

		if (keywordWhat) return keywordWhat;
		if (keywordWhere) return keywordWhere;

		return null;
	});

	public noSearchResults = computed((): boolean => {
		return getters.getTotalSearchResults() === 0;
	});

	public indexOffsetBasedOnCurrentPage = computed((): number => {
		const isFirstPage = this.currentPageNumber.value === 1;
		const offset = (this.currentPageNumber.value - 1) * this.resultsPerPage + 1;

		return isFirstPage ? 1 : offset;
	});

	public searchResults = computed((): TSearchResult[] => {
		const searchResults: SearchStudyCardInfo[] = getters.getSearchResults();

		const presentationSearchResults = searchResults.map((searchResult, index) =>
			this.translateToPresentationSearchStudyCardInfo(searchResult, index + this.indexOffsetBasedOnCurrentPage.value),
		);

		return this.includeBannersForInBetweenStudyCards(presentationSearchResults);
	});

	public onSearchUpdate(): void {
		void this.updateHrefLinks();
	}

	public searchResultsRef: Ref<HTMLElement | null> = ref(null);

	private resultsPerPage: number;
	private pagesWithAdSlotsDefined: number[];
	private impressionValidator: IImpressionValidator;

	public currentPageNumber = computed((): number => {
		return getters.getPageNumber();
	});

	public totalPages = computed((): number => {
		return Math.ceil(store.getters.getTotalSearchResults() / this.resultsPerPage);
	});

	public totalResults = computed((): number => {
		return store.getters.getTotalSearchResults();
	});

	public amountShown = computed((): number => {
		if (this.totalResults.value < this.resultsPerPage || this.isLastPage.value) {
			return this.totalResults.value;
		}
		return this.currentPageNumber.value * this.resultsPerPage;
	});

	public isPortalTypeMasters = computed(() => getters.getPortalType() === PortalType.MASTER);

	public isSearchCard(item: TSearchResult): boolean {
		return item instanceof PresentationStudyCardInfo;
	}

	public toggleSearchHelper(): void {
		if (!this.filterKeywordValue.value) return;
		this.showSearchHelper.value =
			this.filterKeywordValue.value.toLowerCase().includes('phd') ||
				this.filterKeywordValue.value.toLowerCase().includes('master') ||
				this.filterKeywordValue.value.toLowerCase().includes('bachelor') ||
				this.filterKeywordValue.value.toLowerCase().includes('english')
				? true
				: false;
	}

	public setImpressionTracking(): void {
		if (this.applicationStatus.value === ApplicationStatusOptions.READY && this.searchResultsRef.value) {
			this.impressionValidator = new ImpressionValidator(this.searchResultsRef.value, 'SearchStudyCard');

			this.impressionValidator.processObservableElements();
		}
	}

	public async loadNextPage(): Promise<void> {
		const isLoading = this.loading.value;
		const isLastPage = this.isLastPage.value;
		if (!isLoading && !isLastPage) {
			await this.nextPage();
		}
	}

	public async onclickNextButton(): Promise<void> {
		if (this.isLastPage.value) return;

		await this.loadNextPage();
		window.scrollTo(0, 0);
		void this.updateHrefLinks();
	}

	public async onclickPreviousButton(): Promise<void> {
		const isNotFirstPage = this.currentPageNumber.value !== 1;

		if (!isNotFirstPage) return;

		const isLoading = this.loading.value;
		if (!isLoading && isNotFirstPage) {
			await this.previousPage();
		}
		window.scrollTo(0, 0);
		void this.updateHrefLinks();
	}

	private async updateHrefLinks(): Promise<void> {
		const nextPage = await RoutingHelper.resolveToUrl(getters.getFilters(), getters.getQuery(), this.currentPageNumber.value + 1);
		this.nextPageHref.value = nextPage || '';

		if (this.currentPageNumber.value === 1) return;

		const previousPage = await RoutingHelper.resolveToUrl(getters.getFilters(), getters.getQuery(), this.currentPageNumber.value - 1);
		this.previousPageHref.value = previousPage || '';
	}

	public async updatePaginationBar(): Promise<void> {
		await nextTick(() => {
			const countBar = document.getElementById('countBar');
			const countBarWidth = (100 * this.amountShown.value) / (this.totalResults.value || 1);

			if (!countBar) return;
			countBar.style.width = `${countBarWidth}%`;
		});
	}

	private async nextPage(): Promise<void> {
		await store.actions.processPageChange(this.currentPageNumber.value + 1);
	}

	private async previousPage(): Promise<void> {
		await store.actions.processPageChange(this.currentPageNumber.value - 1);
	}

	private addBannerInResults(
		banner: PresentationBannerInfo | PresentationBannerClusterInfo,
		results: TSearchResult[],
		position: number,
	): TSearchResult[] {
		const mergedResults = [...results];
		mergedResults.splice(position, 0, banner);
		return mergedResults;
	}

	private includeBannersForInBetweenStudyCards(searchResults: TSearchResult[]): TSearchResult[] {
		let resultsWithIntesections = [...searchResults];
		resultsWithIntesections = includeBannersInList(resultsWithIntesections);

		if (this.currentPageNumber.value < 2) {
			return resultsWithIntesections;
		}

		for (let pageNumber = 2; pageNumber <= this.currentPageNumber.value; pageNumber++) {
			const numberOfAdsPerPage = 3;
			const totalNumberOfAdsInArray = numberOfAdsPerPage * (pageNumber - 1);
			// at 6th of every page
			const bannerClusterPosition = (pageNumber - 1) * 20 + 6 + totalNumberOfAdsInArray;
			// at 10th of every page
			const bannerPosition = (pageNumber - 1) * 20 + 10 + totalNumberOfAdsInArray;

			const bannerId = `div-gpt-ad-22205299652-${pageNumber}`;
			const mobileBannerId = `div-gpt-ad-22356363616-${pageNumber}`;
			const banner = new PresentationBannerInfo(bannerId, 'BannerPlaceHolderAdvancedVisibility', BannerTypes.ADVANCED);
			const mobileBanner = new PresentationBannerInfo(
				mobileBannerId,
				'BannerPlaceHolderMobileAdvancedVisibility',
				BannerTypes.MOBILE_ADVANCED,
			);

			const bannerClusterId1 = `div-gpt-ad-22235797574-${pageNumber}`;
			const bannerClusterId2 = `div-gpt-ad-22235797574-0${pageNumber}0`;
			const bannerCluster = new PresentationBannerClusterInfo(
				[bannerClusterId1, bannerClusterId2],
				'BannerPlaceHolderTopVisibility',
				BannerTypes.TOP,
			);

			if (resultsWithIntesections.length > bannerClusterPosition) {
				resultsWithIntesections = this.addBannerInResults(banner, resultsWithIntesections, bannerPosition);
				resultsWithIntesections = this.addBannerInResults(mobileBanner, resultsWithIntesections, bannerPosition);
				resultsWithIntesections = this.addBannerInResults(bannerCluster, resultsWithIntesections, bannerClusterPosition);

				const bannerSlotsAlreadyDefined = this.pagesWithAdSlotsDefined.some((page) => page === pageNumber);
				if (!bannerSlotsAlreadyDefined) {
					defineAdSizeAndSlot(banner, [728, 90], bannerId, 0);
					defineAdSizeAndSlot(mobileBanner, [320, 50], mobileBannerId, 0, true);
					defineAdSizeAndSlot(bannerCluster, [300, 250], bannerClusterId1, 0);
					defineAdSizeAndSlot(bannerCluster, [300, 250], bannerClusterId2, 1);

					this.pagesWithAdSlotsDefined.push(pageNumber);
				}
			}
		}
		return resultsWithIntesections;
	}

	private translateToPresentationSearchStudyCardInfo(searchResult: SearchStudyCardInfo, index: number): PresentationStudyCardInfo {
		const canShowIUDiscountInfo = IncentiveVisibilityHelper.canShowIUDiscountInfo(getters.getPortalType(), searchResult);

		return new PresentationStudyCardInfo(searchResult.id, searchResult.card, searchResult.enhanced, index, canShowIUDiscountInfo);
	}
}
